import type { Auth0ContextInterface } from '@auth0/auth0-react';
import { differenceInDays } from 'date-fns/differenceInDays';
import { saveAs } from 'file-saver';
import { type ExceptionLog, ExceptionLogSchema } from '../data/exception-log';
import { type CreateOrder, type Order, OrderSchema } from '../data/order';
import { type OrderItem, OrderItemSchema } from '../data/order-item';
import { type OrderOptions, OrderOptionsSchema } from '../data/order-options';
import { type Product, ProductSchema, saleStat } from '../data/product';
import { type Receipt, ReceiptSchema } from '../data/receipt';
import { type VendorInstructions, VendorInstructionsSchema } from '../data/receiving';
import { type Store, StoreSchema } from '../data/store';
import { type Upload, UploadSchema } from '../data/upload';
import {
  type Companion,
  type UploaderConnection,
  UploaderConnectionSchema,
} from '../data/uploader-connection';
import {
  type Vendor,
  type VendorIntegration,
  VendorIntegrationSchema,
  VendorSchema,
} from '../data/vendor';
import { compare } from '../helpers/column-sort';
import { createMultipleHandler, createSingleHandler, FetchWrapper } from '../helpers/fetch-wrapper';

const baseUrl = `${import.meta.env.VITE_SERVER_URL}/stores`;

export class StoreServiceError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function throwIfNotOk(response: Response): Promise<void> {
  if (!response.ok) {
    const json = await response.json().catch(() => null);
    throw new StoreServiceError(json?.message || response.statusText, response.status);
  }
}

export type StoreCreateInput = {
  owner_first_name: string;
  owner_last_name: string;
};

export class StoreService {
  fetchWrapper: FetchWrapper;

  constructor(tokenAccessor: Auth0ContextInterface['getAccessTokenSilently']) {
    this.fetchWrapper = new FetchWrapper(tokenAccessor);
  }

  getAll(): Promise<Store[]> {
    return this.fetchWrapper
      .get(baseUrl)
      .then(createMultipleHandler(StoreSchema))
      .then((stores: Store[]) => {
        return stores.sort((s1, s2) => s1.name.localeCompare(s2.name));
      });
  }

  getById(id: string): Promise<Store> {
    return this.fetchWrapper.get(`${baseUrl}/${id}`).then(createSingleHandler(StoreSchema));
  }

  update(id: string, parameters: unknown): Promise<Store> {
    return this.fetchWrapper
      .put(`${baseUrl}/${id}`, parameters)
      .then(createSingleHandler(StoreSchema));
  }

  // vendors

  getVendorsByStore(storeId: string): Promise<Vendor[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/vendors`)
      .then(createMultipleHandler(VendorSchema))
      .then((vendors: Vendor[]) => {
        return vendors.sort((v1, v2) => {
          if (v1.disabled === v2.disabled) {
            return v1.name.localeCompare(v2.name);
          }

          return v1.disabled ? 1 : -1;
        });
      });
  }

  updateVendor(storeId: string, vendor: Vendor): Promise<Vendor> {
    return this.fetchWrapper
      .put(`${baseUrl}/${storeId}/vendors/${vendor.vendor_id}`, vendor)
      .then(createSingleHandler(VendorSchema));
  }

  reEnableRepresentative(storeId: string, vendorId: string, email: string): Promise<Vendor> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/vendors/${vendorId}/representatives/reenable`, { email })
      .then(createSingleHandler(VendorSchema));
  }

  getVendorIntegrations(storeId: string): Promise<VendorIntegration[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/vendor-integrations`)
      .then(createMultipleHandler(VendorIntegrationSchema));
  }

  // products

  getProductsByStore(storeId: string): Promise<Product[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/products`)
      .then(createMultipleHandler(ProductSchema))
      .then((products: Product[]) => {
        const today = new Date();

        return products.map((product) => {
          const noStock = product.inventory_qty <= 0;
          const notReceived =
            !product.last_received || differenceInDays(today, product.last_received) >= 365;
          const notSold = saleStat(product, 365) === 0;

          if (noStock && notReceived && notSold) {
            product.auto_disabled = true;
          }

          return product;
        });
      });
  }

  updateProduct(storeId: string, product: Product): Promise<Product> {
    return this.fetchWrapper
      .put(`${baseUrl}/${storeId}/vendors/${product.vendor_id}/products/${product.id}`, product)
      .then(createSingleHandler(ProductSchema));
  }

  // orders

  getOrdersByStore(storeId: string): Promise<Order[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/orders?status=draft,sending,sent,failed`)
      .then(createMultipleHandler(OrderSchema))
      .then((orders: Order[]) => {
        return orders.sort((o1, o2) => compare(o2.updated_at, o1.updated_at));
      });
  }

  getOrderByStore(storeId: string, orderId: string): Promise<Order> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/orders/${orderId}`)
      .then(createSingleHandler(OrderSchema));
  }

  createOrder(storeId: string, order: CreateOrder): Promise<Order> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/orders`, order)
      .then(createSingleHandler(OrderSchema));
  }

  submitOrder(storeId: string, order: Order, method: string): Promise<Order> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/orders/${order.id}/submit`, {
        order,
        send_method: method,
      })
      .then(createSingleHandler(OrderSchema));
  }

  saveOrderOptions(storeId: string, orderId: string, options: OrderOptions): Promise<OrderOptions> {
    return this.fetchWrapper
      .put(`${baseUrl}/${storeId}/orders/${orderId}/options`, options)
      .then(createSingleHandler(OrderOptionsSchema));
  }

  updateOrderItem(storeId: string, orderId: string, orderItem: OrderItem): Promise<OrderItem> {
    return this.fetchWrapper
      .put(`${baseUrl}/${storeId}/orders/${orderId}/item`, orderItem)
      .then(createSingleHandler(OrderItemSchema));
  }

  async downloadOrder(storeId: string, orderId: string, part?: number): Promise<void> {
    const url =
      part !== undefined
        ? `${baseUrl}/${storeId}/orders/${orderId}/download?part=${part}`
        : `${baseUrl}/${storeId}/orders/${orderId}/download`;
    const response = await this.fetchWrapper.get(url);

    if (!response.ok) {
      const json = await response.json();
      throw new Error(`Download failed: ${json?.message || response.statusText}`);
    }

    const disposition = response.headers.get('Content-Disposition');
    const filenameMatch = disposition?.match(/filename="(.+)"/);
    const filename = filenameMatch?.[1] || 'order-download';

    const blob = await response.blob();
    saveAs(blob, filename);
  }

  async deleteOrder(order: Order): Promise<boolean> {
    await this.fetchWrapper.delete(`${baseUrl}/${order.store_id}/orders/${order.id}`);

    return true;
  }

  // uploads

  getUploadsByStore(storeId: string): Promise<Upload[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/uploads`)
      .then(createMultipleHandler(UploadSchema));
  }

  getCompanionConnections(storeId: string): Promise<UploaderConnection[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/companions/connections`)
      .then(createMultipleHandler(UploaderConnectionSchema));
  }

  async getCompanions(storeId: string): Promise<Companion[]> {
    const connections = await this.getCompanionConnections(storeId);

    return connections.filter((c): c is Companion => !!c.uploader);
  }

  triggerUpload(storeId: string): Promise<Upload> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/uploads/trigger`, {})
      .then(createSingleHandler(UploadSchema));
  }

  // exception logs

  getExceptionLogsByStore(storeId: string): Promise<ExceptionLog[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/exception-logs`)
      .then(createMultipleHandler(ExceptionLogSchema))
      .then((logs: ExceptionLog[]) => {
        return logs.sort((a, b) => compare(b.timestamp, a.timestamp));
      });
  }

  // receipts

  getReceipts(storeId: string): Promise<Receipt[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/receipts`)
      .then(createMultipleHandler(ReceiptSchema));
  }

  // receiving

  getVendorInstructions(
    storeId: string,
    vendorNormalizedName: string
  ): Promise<VendorInstructions> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/receiving/instructions/${vendorNormalizedName}`)
      .then(createSingleHandler(VendorInstructionsSchema));
  }

  async claimReceivingSession(
    storeId: string,
    sessionId: string,
    tabId: string
  ): Promise<{ granted: boolean; current_holder?: string }> {
    const response = await this.fetchWrapper.post(
      `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/claim`,
      {},
      { headers: { 'X-Tab-ID': tabId } }
    );
    if (response.status === 409) {
      return response.json();
    }

    await throwIfNotOk(response);

    return response.json();
  }

  generateUploadUrls(
    storeId: string,
    vendorId: string,
    invoices: { filename: string; sha256: string }[]
  ): Promise<{ prefix: string; urls: Record<string, string> }> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/generate-invoices-upload-urls`, {
        vendor_id: vendorId,
        invoices,
      })
      .then((r) => r.json());
  }

  // engagement

  recordEngagement(
    storeId: string,
    eventType: string,
    payload?: Record<string, unknown>
  ): Promise<void> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/events`, {
        event_type: eventType,
        payload,
      })
      .then(() => undefined);
  }
}
